import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Popper,
  ClickAwayListener,
  Paper,
  Stack,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
  Box,
} from '@mui/material';
// import AddIcon from '@mui/icons-material/Add';
import CustomAvatar from '../../../component/CustomAvatar';
import ChatIcon2 from '../../../svg/ChatIcon2';
import ProfileIcon2 from '../../../svg/ProfileIcon2';
import { followCreator } from '../../../utils/apis/memeverse';
// import { toast } from 'react-toastify';
import { useAuth } from '../../../hooks/useAuth';
import { findAndReplaceLinks } from '../../../utils/helper';
import Markdown from '../../userChat/Markdown';
import AIAgentAvatar from '../../../component/AIAgentAvatar';
import { Loader } from '@chatscope/chat-ui-kit-react';
import StarsIcon from '../../../svg/MemeverseIcons/StarsIcon';

const UserProfileHoverDialog = ({ post_user_details, is_requester_following = false, show_following=true, ai_agent = false, is_news = false }) => {
  const navigate = useNavigate();
  const { authUser, setLoginModalVisible } = useAuth();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const [followButton, setFollowButton] = useState<'Follow' | 'Following'>(is_requester_following ? 'Following': 'Follow')
  const [loadingFollow, setLoadingFollow] = useState(false)
  const chatUrl = `/${post_user_details.userName}/chat`;
  const profileUrl = `/${post_user_details.userName}`;

  const isTouch = useMediaQuery('(hover: none), (pointer: coarse)');

  const {
    fullName,
    profilePicUrl,
    shortBio,
    userName,
    user_id,
    is_creator = false,
  } = post_user_details || {};

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (isTouch) {
      setOpen((prev) => !prev);
    }
  };

  const handleFollow = (e) => {

    e.stopPropagation();
    setLoadingFollow(true)
    if (!authUser) {

      setLoadingFollow(false)
      return setLoginModalVisible({ isOpen: true, formType: 'signin', currentStep: 1 })
    } else {
      followCreator(authUser.uid, user_id)
        .then((res) => {
          if (res.ok) {
            return res.json();
          }
        })
        .then((data) => {
          // data has "action": "followed", "docId": "id", "success": "true"
          // just check existence of docId
          if (data.docId) {
            setFollowButton('Following')
          } else {
            setFollowButton('Follow')
          }
        })
        .catch((err) => {
          console.log(err);
        }).finally(() => {
          setLoadingFollow(false)
        })
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (open && isTouch) {
        handleClose();
      }
    };

    if (open && isTouch) {
      window.addEventListener('scroll', handleScroll, true);

      document.addEventListener('touchstart', handleTouchStart, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('touchstart', handleTouchStart, true);
    };
  }, [open, isTouch]); //eslint-disable-line react-hooks/exhaustive-deps

  const handleTouchStart = (e) => {
    if (anchorRef.current && anchorRef.current.contains(e.target)) {
      return;
    }

    const popperElements = document.querySelectorAll('.MuiPaper-root');
    for (let i = 0; i < popperElements.length; i++) {
      if (popperElements[i].contains(e.target)) {
        return;
      }
    }

    handleClose();
  };

  return (
    <div
      ref={anchorRef}
      onClick={handleClick}
      onMouseEnter={!isTouch ? handleOpen : undefined}
      onMouseLeave={!isTouch ? handleClose : undefined}
      style={{
        position: 'relative',
        display: 'inline-block',
        width: 48,
        height: 48,
      }}
      className='user-profile-trigger'
    >
      {ai_agent ?
        <AIAgentAvatar
          src={profilePicUrl}
          sx={{
            width: 48,
            height: 48,
            cursor: 'pointer',
            overflow: 'hidden',
            '& .MuiAvatar-img': {
              objectFit: 'cover',
              objectPosition: 'center 1%',
              transform: 'scale(0.87)',
              transformOrigin: 'center',
            },
          }}
        />
        : is_news
          ? <Box
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                borderRadius: '50%',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <StarsIcon width={24} height={24} fill={theme.palette.text.primary} />
            </Box>
          : <CustomAvatar
              src={profilePicUrl}
              sx={{ width: '100%', height: '100%', cursor: 'pointer'}}
            />
      }

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement='right'
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, -10],
            },
          },
          {
            name: 'flip',
            enabled: true,
            options: {
              fallbackPlacements: ['bottom', 'right', 'left'],
              altBoundary: true,
              rootBoundary: 'viewport',
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: 'viewport',
              padding: 8,
            },
          },
        ]}
        disablePortal={false}
        popperOptions={{
          strategy: 'absolute',
        }}
        sx={{ zIndex: 1400 }}
      >
        {({ TransitionProps }) => (
          <div {...TransitionProps}>
            <ClickAwayListener onClickAway={handleClose} touchEvent="onTouchEnd">
              <Paper
                elevation={4}
                sx={{
                  overflow: 'hidden',
                  width: 320,
                  zIndex: 1300,
                  maxHeight: '80vh',
                  overflowY: 'auto',
                }}
                className="user-profile-popper"
              >
                <Stack
                  sx={{
                    padding: 2,
                    backgroundColor: theme.palette.background.default,
                  }}
                >
                  {/* Avatar and Follow Button */}
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    sx={{ pb: 1.5 }}
                  >

                    {ai_agent ?
                      <AIAgentAvatar
                        src={profilePicUrl}
                        width = {72}
                        height={72}
                        sx={{
                          width: 72,
                          height: 72,
                          overflow: 'hidden',
                          '& .MuiAvatar-img': {
                            objectFit: 'cover',
                            objectPosition: 'center 1%',
                            transform: 'scale(0.87)',
                            transformOrigin: 'center',
                          },
                        }}
                      />
                      : is_news
                        ? <Box
                            sx={{
                              width: 72,
                              height: 72,
                              flexShrink: 0,
                              borderRadius: '50%',
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <StarsIcon width={32} height={32} fill={theme.palette.text.primary} />
                          </Box>
                        : <CustomAvatar
                            src={profilePicUrl}
                            sx={{ width: 72, height: 72 }}
                          />
                    }

                    {show_following && user_id !== authUser?.uid && (
                      <Box sx={{
                        position: 'relative',
                      }}>

                        {/* Make this true for loading animation*/}
                        {false && loadingFollow &&
                        <div style={{
                          'position': 'absolute',
                          'zIndex':'999',
                          'width': '100%',
                          'height': '100%',
                          'display': 'flex',
                          'justifyContent': 'center',
                          'alignItems': 'center'
                          }}>
                            <Loader />
                          </div>
                        }

                        <Button
                          variant='contained'
                          color={followButton === 'Follow' ? 'primary' : 'secondary'}
                          size='medium'
                          onClick={handleFollow}
                        >
                          {followButton}
                        </Button>
                      </Box>
                    )
                    }
                  </Stack>

                  {/* Full Name */}
                  <Typography
                    variant='h7'
                    fontWeight={600}
                    color='text.primary'
                  >
                    {fullName}
                  </Typography>

                  {/* Username */}
                  {!is_news && (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ pb: 1 }}
                    >
                      @{userName}
                    </Typography>
                  )}

                  {/* Bio */}
                  {shortBio && (
                    <Typography
                      component="div"
                      variant='body2'
                      color='text.primary'
                      sx={{ pb: 1 }}
                    >
                      <Markdown className={`markdown`}>
                        {findAndReplaceLinks(shortBio)}
                      </Markdown>
                    </Typography>
                  )}

                  {/* Action Buttons */}
                  {is_creator && !is_news && <Stack
                    direction='row'
                    spacing={2}
                    justifyContent='flex-start'
                  >
                    <Button
                      variant='contained'
                      color='primary'
                      size='medium'
                      id={`button-voice-${userName}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(chatUrl, { state: { useVoiceChat: false } });
                      }}
                      sx={{ zIndex: 2, flex: 1 }}
                      startIcon={
                        <ChatIcon2 fill={theme.palette.primary.contrastText} />
                      }
                    >
                      Chat
                    </Button>

                    <Button
                      variant='contained'
                      color='secondary'
                      size='medium'
                      id={`button-profile-${userName}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(profileUrl);
                      }}
                      sx={{ zIndex: 2, flex: 1 }}
                      startIcon={
                        <ProfileIcon2 fill={theme.palette.secondary.contrastText} />
                      }
                    >
                      View Profile
                    </Button>
                  </Stack>}
                </Stack>
              </Paper>
            </ClickAwayListener>
          </div>
        )}
      </Popper>
    </div>
  );
};

export default UserProfileHoverDialog;
